import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import { useState, useEffect, Fragment, useContext, useMemo } from "react";
import { Button } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import { BsArrowLeft } from "react-icons/bs";
import { BsLockFill, BsFillUnlockFill, BsFillEyeFill } from "react-icons/bs";
import { FaCheck, FaTimes } from "react-icons/fa";
import { BsDownload } from "react-icons/bs";
import { useParams } from "react-router";

import ChartHoc from "../../../analytics/pages/report-details/chart-hoc";
import { IParentData } from "../../../analytics/pages/report-details/report-details";
import CustomCheckbox from "../../../shared/components/custom-checkbox/custom-checkbox";
import { ITableRowData, IColumn, IAnyString } from "../../../shared/components/normal-table/normal-table";
import SimpleModal from "../../../shared/components/simple-modal/simple-modal";
import Spinner from "../../../shared/components/spinner/spinner";
import TextInput from "../../../shared/components/text-input/text-input";
import { default as ToolTipGeneralInfo } from "../../../shared/components/tooltip/tooltip";
import WidgetsLayout, { IConfig, IBlock, IOption } from "../../../shared/components/widgets-layout/widgets-layout";
import { roleWrite } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { getAllDataNonTable, postSingleNonTable, getAllData } from "../../../shared/utils/api";
import { RenderFields } from "../../../shared/utils/render-fields";
import { widgetOptions, reportWidgetSources, modelKeys, sourceDataKeys, crossValidationKeys, activeOptions, textWidgetOptionsReportPDF } from "../../../shared/utils/static-values";
import { useBack } from "../../../shared/utils/use-back";
import { usePrompt } from "../../../shared/utils/use-prompt";
import { validateLoadingStatusNew, rationToPercents2Decimals, filterArrOfObjByString, onlyUnique, randomHexColorCode, defineStep, transformObjectsFracs } from "../../../shared/utils/utils";
import { createUUID, convertKeyIntoName } from "../../../shared/utils/utils";
import NormalTableHoc from "../report-details/normal-table-hoc";
import { populateResultsObj, coreMetrics, metrics6, metrics5, metrics4, subCoreMetrics, subCoreDetailsMetrics, IChartResultsCols } from "../report-details/report-details-columns";
import { convertModelconfigKeyToName, convertModelconfigValueToFrontendValue, modelConfigKeyMapping } from "../report-details/modelconfig-types";
import "./create-pdf.scss";
import { useHttpNonTable } from "../../../shared/hooks/use-httpNonTable";
import { Preview } from "./preview";
import EnumerationField from "@/shared/components/enumeration-field/enumeration-field";

interface ICustomModalProps {
  show: boolean;
  onHide: () => void;
  title: string;
  children?: React.ReactNode;
  confirmButtonText?: string;
  onConfirm?: (() => void) | undefined;
}
interface IModalState {
  isOpen: boolean;
  rowId: string | undefined;
  isBack?: boolean | undefined;
}


const defineOptions = (name: string, selectedThreshold?: number | string) => {
  const result:Record<string, any> =  { // eslint-disable-line
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    stacked: false,
    clip: false,
    plugins: {
      title: {
        display: true,
        text: name,
      },
      tooltip: {
        usePointStyle: true,
        callbacks: {
          label: (context:any) => { // eslint-disable-line
            let label = context.dataset.label;
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              if (
                context.dataset.label.includes("Percentage") ||
                context.dataset.label.includes("ratio")
              ) { 
                label += rationToPercents2Decimals(context.parsed.y);
              } else {
                label += context.parsed.y;
              }
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
      },
      y1: {
        type: "linear" as const,
        ticks: {
          count: 11,
          callback: ((value: any) => { // eslint-disable-line
            return value * 100 + "%";
          })
        },
        
        display: true,
        position: "right" as const,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };
  if (name === "Results") {
    result.scales["x"] = {
      type: "linear" as const,
      display: true,
      ticks: {
        count: 101,
      },
    };
    if (selectedThreshold !== undefined) {
      result.plugins.annotation = {
        annotations: {
          selectedThresholdLine: {
            type: "line",
            xMin: parseFloat(String(selectedThreshold)),
            xMax: parseFloat(String(selectedThreshold)),
            borderColor: "rgba(0,0,0,0.7)",
            borderWidth: 2,
            borderDash: [6, 4],
          }
        }
      };
    } else {
      result.plugins.annotation = { annotations: {} };
    }
  }
  return result;
};

export interface IPreparedItem {
  type?: string,
  content?: string,
  y?: number,
  source?: string;
  columns?: ICol[];
}

export interface ICol { 
  key: string, 
  header: string 
}

export interface ISelectedRowsData {
  fullData: ITableRowData[],
  idsOnly: string[]
}

export interface IChartDataset {
  label: string,
  data: string[] | number[] | string,
  borderColor: string,
  backgroundColor: string,
  key: string,
  yAxisID?: string
}

export interface IMultyChart {
  datasets: IChartDataset[],
  labels : string[]
}

export interface IBtnOption {
  name: string,
  id : string
}

export interface IExtractedKey {
  resultKeysExtr: string[],
  crossValidationKeysExtr: string[],
  historyKeysExtr: string[],
  modelKeysExtr: string[],
  sourceDataKeysExtr: string[]
}

export interface IData {
  className: string,
  items: IBlock[],
  rowHeight: number,
  cols: number,
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CreatePdf = () => {
  const [resultChartData, setResultChartData] = useState<IMultyChart>({labels: [], datasets: []});
  const [extKeys, setExtKeys] = useState<IExtractedKey>({resultKeysExtr:[],crossValidationKeysExtr:[],historyKeysExtr:[],modelKeysExtr:[],sourceDataKeysExtr:[]});
  
  const [unblockInput, setUnblockInput] = useState(false);
  const [lock, setLock] = useState(true);
  const [modal, setModal] = useState(false);
  const [preview, setPreview] = useState<{ blob: Blob; filename: string } | undefined>();
  const [previewModal, setPreviewModal] = useState(false);
  const [filterByVal, setFilterByVal] = useState("");
  const [lastSelectedPicker, setLastSelectedPicker] = useState("");
  const [selectedModalFilteringOptions, setSelectedModalFilteringOptions] = useState<string[]>([]);

  const [historyDataColsWithLabels, setHistoryDataColsWithLabels] = useState(undefined);
  const [historyDataColsWithLabelsTransformed, setHistoryDataColsWithLabelsTransformed] = useState<IBtnOption[] | undefined>(undefined);

  const [lastChangedWidget, setLastChangedWidget] = useState<ITableRowData | undefined>(undefined);

  const [draggingStarted, setDraggingStarted] = useState(false);
  const [modalData, setModalData] = useState<ITableRowData>({id : "", selectedThreshold: undefined, customColumnsColors: {}, tabWidgetId: "", mainLayoutParent: [], selectedStepId: "", steps: [], name: "", textType: "", text: "", widget_type: "", source: "", selectedColumns: []});
  const [layout, setLayout] = useState<IConfig[]>([]);
  const [layoutItems, setlayoutItems] = useState<IBlock[]>([]);
  
  const [resultDataColsWithLabels, setResultDataColsWithLabels] = useState(undefined);
  const [resultDataColsWithLabelsTransformed, setResultDataColsWithLabelsTransformed] = useState<IBtnOption[] | undefined>(undefined);
  const [resultSelectedUniqueCols, setResultSelectedUniqueCols] = useState<undefined | string[]>(undefined);
  const [resultsGlobalData, setResultsGlobalData] = useState<undefined | ITableRowData[]>(undefined);
  const [resultsColsGeneratedColors, setResultsColsGeneratedColors] = useState<IAnyString | undefined>(undefined);

  const [addAfter, setAddAfter] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [latestTemplateId, setLatestTemplateId] = useState("");
  const [modalState, setModalState] = useState<IModalState>({
    isOpen: false,
    rowId: undefined,
  });
  const [saveTemplateModal, setSaveTemplateModal] = useState({show: false, template: "", template_id: ""});
  const [saveAsNewTemplateModal, setSaveAsNewTemplateModal] = useState({show: false, template: "", template_id: ""});
  const [pendingNewTemplateId, setPendingNewTemplateId] = useState<string>("");
  const [selectedModelConfigColumns, setSelectedModelConfigColumns] = useState<{key: string; displayName: string}[]>([]);
  const [layoutInstance, setLayoutInstance] = useState<number>(1);
  const authCtx = useContext(AuthContext);
  const params = useParams();
  const { handleBack } = useBack();
  const { flag, setFlag, next } = usePrompt(isDirty);

  const { sendRequest: getResultsCols, status: getResultsColsStatus, data: getResultsColsData} = useHttp(getAllData, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getReportDetails, status: reportDetailsStatus, data: reportDetailsData, error: reportDetailsError } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, "report", "analytics");
  const { sendRequest: getPdfTemplate, status: pdfTemplateStatusGet, data: pdfTemplateData, error: pdfTemplateErrorGet } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, "layout", "analytics");
  const { sendRequest: getTemplateLayout, status: getTemplateLayoutStatus, data: getTemplateLayoutData} = useHttp(getAllData, false, authCtx.token, "layout", "analytics");
  const { sendRequest: postPdfTemplate, status: pdfTemplateStatusPost, data: postTemplateData, error: postTemplateErrorr} = useHttp(postSingleNonTable, false, authCtx.token, "layout",  "analytics");
  const { sendRequest: postSelectTemplateLayout, status: postSelectTemplateLayoutStatus } = useHttp(postSingleNonTable, false, authCtx.token, undefined, "analytics");
  const { sendRequest: postGeneratePdf, status: generatePdfStatusPost, data: generatePdfData, error: generatePdfErrorPost } = useHttpNonTable(postSingleNonTable, false, authCtx.token, "pdf", "analytics");
  const { sendRequest: postGeneratePdfPreview, status: generatePdfPreviewStatusPost, data: generatePdfPreviewData, error: generatePdfPreviewErrorPost } = useHttpNonTable(postSingleNonTable, false, authCtx.token, "pdf", "analytics");
  const { sendRequest: getModelData, status: modelDataStatus, data: modelData, error: modelDataErrorGet } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined, "analytics");

  const { sendRequest: getResultDataByCols, status: getResultDataByColsStatus, error: getResultDataByColsError, data: getResultDataByColsData } = useHttp(postSingleNonTable, false, authCtx.token, undefined, "analytics");

  const { sendRequest: getHistoryCols, status: getHistoryColsStatus, data: getHistoryColsData, error: getHistoryColsError} = useHttp(getAllData, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getSourceData, status: sourceDataStatus, data: sourceData, error: sourceDataErrorGet } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined, "analytics");
  
  useEffect(() => {
    getReportDetails(`report/${params.id}`);
    getResultsCols(`report_data_columns/${params.id}`);
    getHistoryCols(`history_trend_columns/${params.id}`);
    getModelData(`model/${params.id}`, "?order_by=technical_variable_name asc");
  }, []);

  useEffect(() => {
    if (getHistoryColsStatus === "completed" && getHistoryColsData) {
      setHistoryDataColsWithLabels(getHistoryColsData.rows.columns);
      const prepareReportHistoryData = [];
      for (const [key, value] of Object.entries(getHistoryColsData.rows.columns)) {
        prepareReportHistoryData.push({name: value as string, id: key});
      }
      setHistoryDataColsWithLabelsTransformed(prepareReportHistoryData);
    }
  }, [getHistoryColsStatus, getHistoryColsData, getHistoryColsError]);

  useEffect(() => {
    if (getResultDataByColsStatus === "completed" && !getResultDataByColsError && getResultDataByColsData) {
      const result = getResultDataByColsData.map((resData:ITableRowData, i:number) => {
        if (!resultsGlobalData) {
          return {...resData, id: resData.threshold};
        } else {
          return {...resData, ...resultsGlobalData[i], id: resData.threshold};
        }
      });
      setResultsGlobalData(result);

      const colorsResult:IAnyString = {};
      resultSelectedUniqueCols?.forEach((colKey:string) => {
        colorsResult[colKey] = resultsColsGeneratedColors && resultsColsGeneratedColors[colKey] !== undefined? resultsColsGeneratedColors[colKey] : randomHexColorCode();
      });

      setResultsColsGeneratedColors(colorsResult);
    }
  }, [getResultDataByColsStatus, getResultDataByColsData, getResultDataByColsError]);

  useEffect(() => {
    if (getResultsColsStatus === "completed" && getResultsColsData) {
      setResultDataColsWithLabels(getResultsColsData.rows.columns);

      const prepareReportResultsData = [];
      for (const [key, value] of Object.entries(getResultsColsData.rows.columns)) {
        prepareReportResultsData.push({name: typeof value === "string"? value : "", id: key});
      }
      setResultDataColsWithLabelsTransformed(prepareReportResultsData);
    }
  }, [getResultsColsStatus]);

  useEffect(() => {
    if (reportDetailsStatus === "completed" && reportDetailsData && !reportDetailsError) {
      getTemplateLayout(`template_layout/${reportDetailsData.selected_pdf_template_layout_id}`);
    }
  }, [reportDetailsStatus, reportDetailsData, reportDetailsError]);
    

  useEffect(() => {
    if (getResultDataByColsStatus === "completed" && !modelDataErrorGet && resultsGlobalData && getResultDataByColsData && getResultsColsStatus === "completed" && getResultsColsData) {
      const getResults:IChartResultsCols = populateResultsObj(resultsGlobalData, getResultsColsData);
      setResultChartData(getResults);
    }
  }, [getResultDataByColsStatus, getResultsColsStatus, resultsGlobalData]);

  useEffect(() => {
    if (generatePdfPreviewStatusPost === "completed" && !generatePdfPreviewErrorPost && generatePdfPreviewData) {
      setPreview(generatePdfPreviewData);
    }
  }, [generatePdfPreviewStatusPost, generatePdfPreviewData, generatePdfPreviewErrorPost]);

  useEffect(() => {
    if (generatePdfStatusPost === "completed" && !generatePdfErrorPost && generatePdfData) {
      const url = window.URL.createObjectURL(generatePdfData.blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", generatePdfData.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }, [generatePdfStatusPost]);

  useEffect(() => {
    if (modelDataStatus === "completed" && modelData && !modelDataErrorGet) {
      setModalData((prev) => ({...prev, modelData: modelData}));
    }
  }, [modelDataStatus, modelData, modelDataErrorGet]);

  useEffect(() => {
    if (reportDetailsStatus === "completed" && !reportDetailsError && reportDetailsData) {
      getPdfTemplate(`template_layout/${reportDetailsData.selected_pdf_template_layout_id}`);
    }
  }, [reportDetailsStatus, reportDetailsData, reportDetailsError]);

  useEffect(() => {
    if (sourceDataStatus === "completed" && !sourceDataErrorGet && sourceData) {
      const extractedKeys = extractKeyFromDataObj(sourceData,"sourceDataKeysExtr");
      setExtKeys({...extKeys, ...extractedKeys});
    }
  }, [sourceDataStatus, sourceData, sourceDataErrorGet]);

  useEffect(() => {
    if (modelDataStatus === "completed" && !modelDataErrorGet && modelData) {
      const extractedKeys = extractKeyFromDataObj(modelData,"modelKeysExtr");
      setExtKeys({...extKeys, ...extractedKeys});
    }
  }, [modelDataStatus, modelData, modelDataErrorGet]);

  useEffect(() => {
    if (layoutInstance !== 0 && layoutItems && layoutItems.some(item => item.optionName === "Model Configuration")) {
      setLayoutInstance(0);
      const modelConfig = layoutItems.find(item => item.optionName === "Model Configuration");
      if (modelConfig?.selectedColumns) {
        setSelectedModelConfigColumns(
          modelConfig.selectedColumns ? modelConfig.selectedColumns.map((col: any) => ({ // eslint-disable-line
            key: col,
            displayName: col
          })) : selectedModelConfigColumns
        );
      }
    }
    const checkModalConfigExist = layoutItems.find((item) => item.optionName === "Model Configuration");
    if (!checkModalConfigExist && layoutInstance === 1) {
      setSelectedModelConfigColumns([]);
    }
  }, [layoutItems]);

  useEffect(() => {
    const rolesArray = authCtx.selectedTenant.roles;
    if (rolesArray.indexOf(roleWrite) !== -1) {
      setUnblockInput(true);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    if (pdfTemplateStatusGet === "completed" && !pdfTemplateErrorGet) {
      if ("layoutItems" in pdfTemplateData.layout && "layout" in pdfTemplateData.layout) {
        setLayout(pdfTemplateData.layout.layout);
        setlayoutItems(pdfTemplateData.layout.layoutItems);
        setLock(pdfTemplateData.layout.lock);
      }
    }
  }, [pdfTemplateStatusGet, pdfTemplateData, pdfTemplateErrorGet]);

  useEffect(() => {
    if (pdfTemplateStatusPost === "completed" && !postTemplateErrorr && postTemplateData) { 
      setLock(true);
      setIsDirty(false);
      setFlag(false);
      setModalState({ isOpen: false, rowId: undefined });
      next();
    }
    }, [pdfTemplateStatusPost, postTemplateData, postTemplateErrorr]);
    

  useEffect(function () {
    if (saveAsNewTemplateModal.template_id && reportDetailsData) {
      setPendingNewTemplateId(saveAsNewTemplateModal.template_id);
      postSelectTemplateLayout(
        `select_template_layout/${reportDetailsData.dataset_id}/analytics_report_pdf_template`,
        {
          selected_template_layout_id: saveAsNewTemplateModal.template_id,
        },
        undefined,
        "none"
      );

      setSaveAsNewTemplateModal({
        show: false,
        template: "",
        template_id: "",
      });
    }
  }, [saveAsNewTemplateModal.template_id]);

  useEffect(() => {
    if (getTemplateLayoutStatus === "completed" && latestTemplateId) {
      setLatestTemplateId(reportDetailsData.selected_template_layout_id);
    }
  }, [getTemplateLayoutStatus, latestTemplateId]);

  useEffect(() => {
    let resultToBeFetch = false;
    let modelToBeFetch = false;
    let sourceDataToBeFetch = false;
    let allCollsResult:string[] = [];

    layoutItems.forEach((item) => {
      if (
        item.optionName === "Custom widget"
      ) {
        if (item.settings?.source === "report_data") {
          allCollsResult = [...allCollsResult, ...item.settings.selectedColumns];
          resultToBeFetch = true;
        }
        if (item.settings?.source === "model") {
          modelToBeFetch = true;
        }
        if (item.settings?.source === "source_data") {
          sourceDataToBeFetch = true;
        }
      }
      else if (item.optionName === "Tabs Widget" && item.steps.length !== 0) {
        item.steps.forEach((step) => {
          step.layoutItems.forEach((item) => {
            if (item.settings?.source === "report_data") {
              allCollsResult = [...allCollsResult, ...item.settings.selectedColumns];
              resultToBeFetch = true;
            }
            if (item.settings?.source === "model") {
              modelToBeFetch = true;
            }
            if (item.settings?.source === "source_data") {
              sourceDataToBeFetch = true;
            }
          });
        });
      }
    });

    if (getResultDataByColsStatus !== "pending" && (resultDataColsWithLabelsTransformed || resultToBeFetch)) {
      allCollsResult = allCollsResult.filter(onlyUnique);
      // "threshold" is a fixed col, so we want to ignore it in the general flow
      allCollsResult = allCollsResult.filter(colData => colData !== "threshold");
      setResultSelectedUniqueCols(allCollsResult);

      if (getResultDataByColsData && resultSelectedUniqueCols && lastChangedWidget?.widgetType !== "table") {
        const newlyAddedMetricsOnly = allCollsResult.filter(x => !resultSelectedUniqueCols.includes(x));
        newlyAddedMetricsOnly.length !== 0 && 
        getResultDataByCols(`report_data/${params.id}`, {
          columns: newlyAddedMetricsOnly.filter((colll) => getResultsColsData?.rows?.columns[`${colll}`])
        });
      } else if (!getResultDataByColsData && getResultsColsData) {
        // table
        getResultDataByCols(`report_data/${params.id}?order_by=threshold asc`, {
          columns: allCollsResult.filter((colll) => getResultsColsData?.rows?.columns[`${colll}`])
        }, undefined, "none");
      }
    }
    if (modelDataStatus === "completed" && ((modalData.source === "model" && extKeys.modelKeysExtr.length === 0) || (modelToBeFetch && !modelData))) {
      getModelData(`model/${params.id}`, "?order_by=technical_variable_name asc");
    }
    if (sourceDataStatus !== "pending" && ((modalData.source === "source_data" && extKeys.sourceDataKeysExtr.length === 0) || (sourceDataToBeFetch && !sourceData))) {
      getSourceData(`source_data/${params.id}`);
    }
  }, [modalData.source, layoutItems, lastChangedWidget, getResultsColsData]);
  
  useEffect(() => {
    if (postSelectTemplateLayoutStatus === "completed" && !postTemplateErrorr) {
      const idToFetch = pendingNewTemplateId || reportDetailsData.selected_pdf_template_layout_id;
      setPendingNewTemplateId("");
      getTemplateLayout(`template_layout/${idToFetch}`);
    }}, [postSelectTemplateLayoutStatus]);
  
    const extractKeyFromDataObj = (fetchedData: any, keyName: string) => { // eslint-disable-line
    if (!fetchedData) { return {[keyName]:[]};}
    const fetchedDataKeysExtr = {...fetchedData[0]};
    delete fetchedDataKeysExtr["id"];
    delete fetchedDataKeysExtr["report_id"];
    return { [keyName]: Object.keys(fetchedDataKeysExtr) };
  };

  const formatModelConfigKey = (key: string) => {
    return modelConfigKeyMapping[key as keyof typeof modelConfigKeyMapping] || key;
  };

  const toDisplayModelConfig = (val: any): string => { // eslint-disable-line
    if (val == null){
      return "";
    }

    if (typeof val === "string" || typeof val === "number") {
      return convertModelconfigValueToFrontendValue.get(String(val)) || String(val);
    }

    if (typeof val === "boolean") {
      return val ? "Yes" : "No";
    }

    if (Array.isArray(val)) {
      return val.map((item) => toDisplayModelConfig(item)).join(", ");
    }

    if (typeof val === "object") {
      try {
        return JSON.stringify(val);
      } catch {
        return String(val);
      }
    }

    return String(val);
  };

  const saveAsNewTemplateTemplateModal = useMemo(() => {
    const newID = createUUID();
    return (
      <TextInput
        type={"text"}
        id={newID}
        required={true}
        inputKey={"template"}
        autofocus={false}
        controlled={true}
        value={saveAsNewTemplateModal.template}
        placeholder={"Enter template name"}
        handleOnChange={(targetVal) => setSaveAsNewTemplateModal({...saveAsNewTemplateModal, template: targetVal})}
      />
    );
  }, [saveAsNewTemplateModal]);

  const setModalDataFromBlock = (id: string, layoutItemsLoc: IBlock[], mainLayoutParent: IBlock[], newTabWidget?: boolean, tabWidgetEdit?: string, addAfter?: boolean) => {
    const findItemBlock = layoutItemsLoc.find((block:IBlock) => block.id === id);
    if (addAfter) {
      setModalData({ 
        id,
        addAfter: true
      });
    } else {
      setModalData({ 
        mainLayoutParent,
        id,
        customColumnsColors: findItemBlock?.settings?.customColumnsColors ? {...findItemBlock?.settings?.customColumnsColors} : {},
        tabWidgetId: "",
        steps: findItemBlock?.steps,
        selectedThreshold: findItemBlock?.settings?.selectedThreshold,
        name: findItemBlock?.optionName,
        textType: findItemBlock?.textType? findItemBlock.textType : "",
        text: findItemBlock?.text? findItemBlock?.text : "",
        ...findItemBlock?.settings
      });
    }
  };

  const getLayoutChanges = (layout: IConfig[]) => {
    setLayout(layout);
    if (
      reportDetailsData?.selected_pdf_template_layout_id &&
      JSON.stringify(layout) === JSON.stringify(pdfTemplateData?.layout.layout) &&
      JSON.stringify(layoutItems) === JSON.stringify(pdfTemplateData?.layout.layoutItems)
    ){
      setIsDirty(false);
    }
    else {
      setIsDirty(true);
    }
  };

  const renderText = (id: string, layoutItemsLoc : IBlock[]) => {
    const findIdMatch = layoutItemsLoc? layoutItemsLoc.filter((item:IBlock) => item.id === id) : [];
    if (!findIdMatch[0]) { 
      return <div>loading...</div>;
    }
    if (findIdMatch[0].textType === "header") {
      return <h1 className="reportDetails-labelWrapper">{findIdMatch[0].text}</h1>;
    }
    else if (findIdMatch[0].textType === "subheader") {
      return <h2 className="reportDetails-labelWrapper">{findIdMatch[0].text}</h2>;
    }
    else if (findIdMatch[0].textType === "text") {
      return <p className="reportDetails-labelWrapper">{findIdMatch[0].text}</p>;
    }
  };

  const getNewColumnsOrder = (data: IParentData, order: IColumn[]) => {
    const onlyKeys = order.map((column:IColumn) => column.key);
    if  (data.parentTabsWidgetData.length === 0) {
      const layoutItemsClone:IBlock[] = layoutItems.map((item) => {
        if (item.id === data.widgetId) {
          return {...item, settings: item.settings? {...item.settings, selectedColumns: onlyKeys} : undefined};
        }
        return item;
      });
      setlayoutItems(layoutItemsClone);
    }
  };

  const renderCustomWidget = (id: string, layoutItemsLoc:IBlock[], layoutLoc:IConfig[], tabsWidgetData?: IBlock[]) => {
    const findIdMatch = layoutItemsLoc.find((item:IBlock) => item.id === id);
    // fallback
    if (!findIdMatch) { 
      return <div>loading...</div>;
    }
    const selectedSource = findIdMatch.settings?.source;
    const selectedType  = findIdMatch.settings?.widget_type;

    let modelConfigurationData: any[] = []; // eslint-disable-line
    if (selectedSource === "model_configuration") {
      if (!reportDetailsData?.modelconfig_content) {
        return <div>loading...</div>;
      }
      try {
        const row = JSON.parse(reportDetailsData.modelconfig_content);
        modelConfigurationData = Object.keys(row).map(key => ({
          configurationItem: key,
          value: row[key]
        }));
      } catch (error) {
        console.error("Error parsing model configuration data:", error);
        return <div>Error loading model configuration</div>;
      }
    }

    const constructData: any = { // eslint-disable-line
      model: modelData? modelData : [],
      source_data: sourceData? sourceData : [], 
      report_data: resultsGlobalData? resultsGlobalData : [], 
      history: [],
      model_configuration: modelConfigurationData,
    };

    let preparedResults =  [...constructData[`${selectedSource}`]];

    if (selectedSource === "report_data") {
      preparedResults = preparedResults.map(row => {
        const prepareResult = transformObjectsFracs(row);
        return {
          ...prepareResult,
          id: row.threshold,
        };
      });
    }

    const generatedReportColumns:IColumn[] = resultDataColsWithLabelsTransformed? resultDataColsWithLabelsTransformed.map((locData: ITableRowData) => {
      const findKeyOrConvertKeyIntoName = resultDataColsWithLabels && resultDataColsWithLabels[locData.id]? resultDataColsWithLabels[locData.id] : convertKeyIntoName(locData.id);
      return { key:locData.id, displayName: findKeyOrConvertKeyIntoName, dataType: {type: "text" } };
    }) : [];
    const generatedModelColumns:IColumn[] = extKeys.modelKeysExtr.map((key: string) => { return { key, displayName: convertKeyIntoName(key), dataType: {type: "text" } };});
    const generatedSourceColumns:IColumn[] = extKeys.sourceDataKeysExtr.map((key: string) => { return { key, displayName: convertKeyIntoName(key), dataType: {type: "text" } };});
    
    const generatedHistoryColumns:IColumn[] = historyDataColsWithLabelsTransformed? historyDataColsWithLabelsTransformed.map((locData: ITableRowData) => {
      const findKeyOrConvertKeyIntoName = historyDataColsWithLabels && historyDataColsWithLabels[locData.id]? historyDataColsWithLabels[locData.id] : convertKeyIntoName(locData.id);
      return { key:locData.id, displayName: findKeyOrConvertKeyIntoName, dataType: {type: "text" } };
    }) : [];

    const generatedModelConfigurationColumns:IColumn[] = selectedSource === "model_configuration" && modelConfigurationData.length > 0
      ? Object.keys(JSON.parse(reportDetailsData.modelconfig_content)).map((key: string) => {
        const displayName = convertModelconfigKeyToName.get(key) ?? convertKeyIntoName(key);
        return { key, displayName, dataType: {type: "text" } };
      })
      : [];

    const allColumnsWithIds = [
      { columns: generatedReportColumns, source: "report_data", defaultSorting: "threshold"},
      { columns: generatedModelColumns, source: "model", defaultSorting: "technical_variable_name"},
      { columns: generatedSourceColumns, source: "source_data"},
      { columns: generatedHistoryColumns, source: "history", defaultSorting: "timeframe"},
      { columns: generatedModelConfigurationColumns, source: "model_configuration", defaultSorting: "configurationItem"}
    ];

    const findScaleinLayout = layoutLoc.filter((block:IConfig) => block.i === id);
    const matchSourceWithColumns = allColumnsWithIds.filter((data) => data.source === selectedSource);
    const onlySelectedColumns = matchSourceWithColumns[0].columns.filter(col => findIdMatch.settings?.selectedColumns.indexOf(col.key) !== -1);

    const orderAccordingToRemote:IColumn[] | undefined = findIdMatch.settings?.selectedColumns.map((col:string) => {
      if (findIdMatch.settings?.source === "report_data") {
        const findKeyOrConvertKeyIntoName = resultDataColsWithLabels && resultDataColsWithLabels[col]? resultDataColsWithLabels[col] : convertKeyIntoName(col);
        return { key: col, displayName: findKeyOrConvertKeyIntoName, dataType: {type: "text" } };
      } else if (findIdMatch.settings?.source === "model_configuration") {
        const displayName = convertModelconfigKeyToName.get(col) ?? convertKeyIntoName(col);
        return { key: col, displayName, dataType: {type: "text" } };
      } else {
        return { key: col, displayName: convertKeyIntoName(col), dataType: {type: "text" } };
      }
    });
  
    if (selectedType === "table") {
      let defineAllColsOfSource:any = {}; // eslint-disable-line

      if (findIdMatch.settings?.source === "model" || findIdMatch.settings?.source === "source_data") {
        defineAllColsOfSource = matchSourceWithColumns[0].columns.forEach((colData) => {
          defineAllColsOfSource = {...defineAllColsOfSource, [`${colData.key}`] : colData.displayName};
        });
      } else if (findIdMatch.settings?.source === "model_configuration") {
        defineAllColsOfSource = {};
        matchSourceWithColumns[0].columns.forEach((colData) => {
          defineAllColsOfSource = {...defineAllColsOfSource, [`${colData.key}`] : colData.displayName};
        });
      } else if (findIdMatch.settings?.source === "report_data")  {
        defineAllColsOfSource = getResultsColsData?.rows?.columns;
        if (!getResultsColsData) {
          return;
        }
      } else if (findIdMatch.settings?.source === "history") {
        defineAllColsOfSource = getHistoryColsData?.rows?.columns;
        if (!getHistoryColsData) {
          return;
        }
      }

      if (selectedSource === "model_configuration") {
        const selectedColumns = orderAccordingToRemote ? orderAccordingToRemote : onlySelectedColumns;
        const rowData = JSON.parse(reportDetailsData.modelconfig_content);

        return (
          <div className="reportDetailsClassic-modelConfigTableWrapper" style={{height: `${findScaleinLayout.length !== 0 ?  ((findScaleinLayout[0].h * 25 ) - 10) : 180}px`, overflow: 'auto'}}>
            <table className="reportDetailsClassic-modelConfigTable">
              <thead className="reportDetailsClassic-modelConfigTableThead">
                <tr className="reportDetailsClassic-modelConfigTableTheadTr">
                  <th className="reportDetailsClassic-modelConfigTableTheadTh">Configuration Item</th>
                  <th className="reportDetailsClassic-modelConfigTableTheadTh">Value</th>
                </tr>
              </thead>
              <tbody className="reportDetailsClassic-modelConfigTableTbody">
                {selectedColumns.map(({ key }) => {
                  const value = rowData[key];
                  if (value === null || value === undefined || value === "") {
                    return null;
                  }

                  const isBool = typeof value === "boolean";
                  const display = isBool ? "" : toDisplayModelConfig(value);

                  return (
                    <tr className="reportDetailsClassic-modelConfigTableTbodyTr" key={key}>
                      <td className="reportDetailsClassic-modelConfigTableTbodyTd">
                        {formatModelConfigKey(key)}
                      </td>
                      <td className="reportDetailsClassic-modelConfigTableTbodyTd">
                        {isBool ? (value ? <FaCheck /> : <FaTimes />) : display}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }

      return (
        <NormalTableHoc
          orderSettings={{parentTabsWidgetData: tabsWidgetData? tabsWidgetData : [], widgetId: id}}
          draggingStarted={draggingStarted}
          getDraggingStatus={(toggle: boolean) => setDraggingStarted(toggle)}
          selectedSource={selectedSource}
          columns={orderAccordingToRemote ? orderAccordingToRemote : onlySelectedColumns}
          allColumns={defineAllColsOfSource}
          fetchedData={{ rows: preparedResults }}
          lock={lock}
          widgetData={findIdMatch}
          selectedThreshold={findIdMatch?.settings?.selectedThreshold}
          onlySelectedThreshold={findIdMatch?.settings?.show_only_for_threshold}
          getNewColumnsOrder={getNewColumnsOrder}
          allStatus="completed"
          dataUrl={"reports/:id"}
          widgetId={findIdMatch?.id}
          lastChangedWidget={lastChangedWidget}
          defaultSortCol={matchSourceWithColumns[0].defaultSorting}
          hideSearchInput={true}
          getResultDataByColsStatus={getResultDataByColsStatus}
          unblockInput={unblockInput}
          hideCheckBoxes={true}
          hideButtons={[]}
          scale={{height: `${findScaleinLayout.length !== 0 ?  ((findScaleinLayout[0].h * 45 ) - 50) : 180}`, width: "auto"}}
        /> 
      );
    }

    const resultDataPrep = {...resultChartData, datasets: resultChartData.datasets.filter((dataset) => findIdMatch.settings?.selectedColumns.indexOf(dataset.key) !== -1)};
    
    const applySelectedColor = (data: IMultyChart) => {
      return {
        ...data,
        datasets: data.datasets.map((col:IChartDataset) => {
          const findSelectedColor = findIdMatch.settings?.customColumnsColors?.[col.key];
          const colorResult = findSelectedColor? findSelectedColor : col.backgroundColor;
          return {...col, backgroundColor: colorResult, borderColor: colorResult};
        })
      };
    };

    if (selectedType === "chart" && selectedSource === "history" && getHistoryColsData) {
      return <ChartHoc allHistoryCols={getHistoryColsData}  widgetData={findIdMatch} options={defineOptions("History trends")} applySelectedColor={applySelectedColor} />;
    }

    if (selectedType === "chart" && selectedSource === "report_data") {
      return (
        <div className="reportDetailsClassic-chart show">
          <Line options={defineOptions("Results", reportDetailsData?.selected_threshold)} data={applySelectedColor(resultDataPrep)} />
        </div>
      );
    }
  };

  const renderOptionsBtns = (options: IOption[]) => {
    return options.map((option: IOption, i: number) => (
      <div 
        key={i} 
        className="blank" 
        style={{zIndex: 10}}
        onClick={() => {
          if (option.name === "Custom widget" || option.name === "Text" || option.name === "Model Configuration") {
        setModal(true);
        setModalData({...modalData, name: option.name});
          } else {
        onBlockAdd(option.name, option.settings);
          }
        }}
      >
        {option.displayName} +
      </div>
    ));
  };

  const onBlockAdd = (name: string, settings: IBlock['settings']) => {
    const newID = createUUID();
    const withNewItem = [
      ...layoutItems,
      {
        optionName: name,
        id: newID,
        settings,
        steps: []
      },
    ];
    setlayoutItems(withNewItem);
    !lock && setIsDirty(true);
  };

  const optionsBlocks = [
    {
      name: "Text",
      displayName: "Text",
      component: renderText,
      settings: undefined,
      editable: true
    },
    {
      name: "Custom widget",
      displayName: "Add widget",
      component: renderCustomWidget,
      settings: undefined,
      editable: true
    }
  ];

  const handleSubmit = () => {
    let setId = modalData.id;

    const selectedColsWithThresholdCol =
      modalData?.selectedColumns?.indexOf("threshold") === -1 && modalData.widget_type === "table" && modalData.source === "report_data"
        ? ["threshold", ...modalData.selectedColumns]
        : modalData.selectedColumns;

    if (setId === "" || modalData.addAfter) {
      setAddAfter(setId);
      // new main widget
      setId = createUUID();
      setLastChangedWidget({id: setId, widgetType: modalData.widget_type, changed: lastChangedWidget? !lastChangedWidget.changed : true});
      setlayoutItems([
        ...layoutItems,
        {
          optionName: modalData.name,
          id: setId,
          text: modalData.text,
          textType: modalData.textType,
          steps: modalData.steps,
          selectedStepId: modalData.name === "Tabs Widget" ? modalData.steps[0].key : "",
          settings: modalData.name === "Custom widget"?
            {
              widget_type: modalData.widget_type,
              source: modalData.source,
              selectedThreshold: modalData.source !== "history"? undefined : modalData.selectedThreshold,
              selectedColumns: selectedColsWithThresholdCol,
              customColumnsColors: modalData.name === "Custom widget"?  (modalData?.customColumnsColors ? {...modalData.customColumnsColors} : {}) : {},
              show_only_for_threshold: modalData.show_only_for_threshold,
            }
            : undefined
        },
      ]);
    } else {
    // edit main widgets
      const result = layoutItems.map((item) => {
        if (item.id === setId) {
          return {
            optionName: modalData.name,
            id: setId,
            textType: modalData.textType,
            text: modalData.text,
            steps: modalData.steps,
            settings: modalData.name === "Custom widget" ? 
              {
                widget_type: modalData.widget_type, 
                source: modalData.source,
                selectedThreshold: modalData.source !== "history"? undefined : modalData.selectedThreshold,
                selectedColumns: modalData.selectedColumns,
                customColumnsColors: modalData.customColumnsColors ? {...modalData.customColumnsColors} : {},
                show_only_for_threshold: modalData.show_only_for_threshold
              } 
              : undefined
          };
        } else {
          return item;
        }
      });
      setlayoutItems(result);
      setLastChangedWidget({id: setId, widgetType: modalData.widget_type, changed: lastChangedWidget? !lastChangedWidget.changed : true});
    }
    setFilterByVal("");
    setModal(false);
    setSelectedModalFilteringOptions([]);
    setModalData({id : "", selectedThreshold: undefined, customColumnsColors: {}, mainLayoutParent: [], tabWidgetId: "", steps: [], name: "", text: "", textType: "", widget_type: "", source: "", selectedColumns: []});
  };

  const handleModalClosed = () => {
    setModal(false);
    setAddAfter("");
    setFilterByVal("");
    setSelectedModalFilteringOptions([]);
    setModalData({id : "", selectedThreshold: undefined, customColumnsColors: {}, mainLayoutParent: [], tabWidgetId: "", steps: [], name: "", text: "", textType: "", widget_type: "", source: "", selectedColumns: []});
  };

  const handleOnChange = (targetVal: string, inputKey: string) => {
    if (
      (inputKey === "source" && targetVal !== modalData.source && modalData.source !== "") ||
      (inputKey === "widget_type" && targetVal !== modalData.widget_type && modalData.widget_type !== "")
    ) {
      // when switching source or type, empty selected colums
      setModalData((prevState) => ({ ...prevState, [inputKey]: targetVal, selectedColumns: [] }));
    } else {
      setModalData((prevState) => ({ ...prevState, [inputKey]: targetVal }));
    }
  };

  const handleOnSelectColumn = (key: string) => {  
    let result = [...modalData.selectedColumns];
    if (result.indexOf(key) === -1) {
      if (resultsColsGeneratedColors && !resultsColsGeneratedColors[key]) {
        setResultsColsGeneratedColors({...resultsColsGeneratedColors, [key] : randomHexColorCode()});
      }
      result = [...result, key];
    } else {
      result = result.filter((selectedKey) => selectedKey !== key);
    }
    setModalData({...modalData, selectedColumns: result});
  };

  const handleOnSelectColumnColor = (key: string, color: string, defaultColor?: undefined | string) => {
    setModalData({...modalData, customColumnsColors: { ...modalData.customColumnsColors, [key] : color? color : defaultColor }});
  };

  const sortWidgetButtons = (columns: IBtnOption[]) => {
    return columns.sort((a, b) => a.id.localeCompare(b.id));
  };

  const onModalClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    const filteredId = (event.target as Element).id.replace("arrow-","");

    if (filteredId !== lastSelectedPicker &&  lastSelectedPicker) {
      setLastSelectedPicker(filteredId);
    }
    else if (filteredId !== lastSelectedPicker && (event.target as Element).className !== "react-colorful__interactive" && lastSelectedPicker !== "") {
      setLastSelectedPicker("");
    }
  };

  const widgetColumnsBtns = (source?: string, mode?: string) => {
    if (
      source === "report_data" && !getResultDataByColsData && !resultDataColsWithLabelsTransformed ||
      source === "model" && !modelData ||
      source === "source_data" && !sourceData ||
      source === "history" && !getHistoryColsData && !historyDataColsWithLabelsTransformed ||
      source === "model_configuration" && !reportDetailsData?.modelconfig_content
    ) {
      return "";
    }

    let modelConfigColumns: IBtnOption[] = [];
    if (source === "model_configuration") {
      try {
        const row = JSON.parse(reportDetailsData.modelconfig_content);
        modelConfigColumns = Object.keys(row).map((key: string) => ({
          id: key,
          name: convertModelconfigKeyToName.get(key) ?? key,
        }));
      } catch (error) {
        console.error("Error parsing model configuration data:", error);
        return "";
      }
    }

    const result = {
      resultKeysExtr: resultDataColsWithLabelsTransformed? resultDataColsWithLabelsTransformed : [],
      crossValidationKeysExtr: sortWidgetButtons(extKeys.crossValidationKeysExtr.map((key:string) => {
        const result = crossValidationKeys.find((resKey) => resKey.id === key);
        return result ? result : {name: key, id: key};
      })),
      historyKeysExtr: historyDataColsWithLabelsTransformed? historyDataColsWithLabelsTransformed : [],
      modelKeysExtr: sortWidgetButtons(extKeys.modelKeysExtr.map((key:string) => {
        const result = modelKeys.find((resKey) => resKey.id === key);
        return result ? result : {name: key, id: key} ;
      })),
      sourceDataKeysExtr: sortWidgetButtons(extKeys.sourceDataKeysExtr.map((key:string) => {
        const result = sourceDataKeys.find((resKey) => resKey.id === key); 
        return result ? result : {name: key, id: key};
      })),
      modelConfigKeysExtr: sortWidgetButtons(modelConfigColumns)
    };

    if (source === "report_data") {
      let filteredResult: IBtnOption[] = [];
      if (mode === "onlySelected") {
        result.resultKeysExtr.forEach((col: IBtnOption) => {
          if (modalData.selectedColumns.indexOf(col.id) !== -1) {
            filteredResult.push(col);
          }
        });
        filteredResult = filterByVal ? filterArrOfObjByString(filteredResult, "name", filterByVal) : filteredResult;
      } else {
        result.resultKeysExtr.forEach((col: IBtnOption) => {
          if (!selectedModalFilteringOptions.some((selectedOption) => !col.id.includes(selectedOption))) {
            filteredResult.push(col);
          }
        });
        filteredResult = filterByVal ? filterArrOfObjByString(filteredResult, "name", filterByVal) : filteredResult;
      }

      return filteredResult.map((key: IBtnOption) => (
        <CustomCheckbox
          withInnerHeight={modalData.widget_type === "chart" ? "#modalInputsWrapper" : undefined}
          lastSelectedPicker={modalData.widget_type === "chart" ? lastSelectedPicker : undefined}
          setLastSelectedPicker={modalData.widget_type === "chart" ? (keyId: string) => setLastSelectedPicker(keyId) : undefined}
          handleOnSelectColumnColor={modalData.widget_type === "chart" ? handleOnSelectColumnColor : undefined}
          selectedColor={modalData.widget_type === "chart" ? (modalData.customColumnsColors?.[key.id] ? modalData.customColumnsColors[key.id] : "") : undefined}
          defaultColor={modalData.widget_type === "chart" ? (resultsColsGeneratedColors ? resultsColsGeneratedColors[key.id] : "") : undefined}          
          initialValue={modalData.selectedColumns.indexOf(key.id) !== -1}
          simple={true}
          key={key.name}
          handleOnSelectRole={handleOnSelectColumn}
          id={key.id}
          text={key.name}
        />
      ));
    }

    if (source === "model") {
      let filteredResult:IBtnOption[] = [];
      if (mode === "onlySelected") {
        result.modelKeysExtr.forEach((col:IBtnOption) => {
          if (col.id !== "threshold" && modalData.selectedColumns.indexOf(col.id) !== -1) {
            filteredResult.push(col);          
          } 
        });
        
        filteredResult = filterByVal? filterArrOfObjByString(filteredResult, "name", filterByVal) : filteredResult;
      } else {
        result.modelKeysExtr.forEach((col:IBtnOption) => {
          if (col.id !== "threshold" && !selectedModalFilteringOptions.some((selectedOption) => !col.id.includes(selectedOption))) {
            filteredResult.push(col);
          } 
        });
        filteredResult = filterByVal? filterArrOfObjByString(filteredResult, "name", filterByVal) : filteredResult;
      }
      
      return filteredResult.length > 0 
        ? filteredResult.map((key:IBtnOption) => <CustomCheckbox initialValue={modalData.selectedColumns.indexOf(key.id) !== -1}  simple={true} key={key.name} handleOnSelectRole={handleOnSelectColumn} id={key.id} text={key.name}/>)
        : <div>No data available</div>;
    }
    if (source === "model_configuration") {
      let filteredResult:IBtnOption[] = [];
      if (mode === "onlySelected") {
        result.modelConfigKeysExtr.forEach((col:IBtnOption) => {
          if (modalData.selectedColumns.indexOf(col.id) !== -1) {
            filteredResult.push(col);
          }
        });
        filteredResult = filterByVal? filterArrOfObjByString(filteredResult, "name", filterByVal) : filteredResult;
      } else {
        result.modelConfigKeysExtr.forEach((col:IBtnOption) => {
          if (!selectedModalFilteringOptions.some((selectedOption) => !col.id.includes(selectedOption))) {
            filteredResult.push(col);
          }
        });
        filteredResult = filterByVal? filterArrOfObjByString(filteredResult, "name", filterByVal) : filteredResult;
      }

      return filteredResult.length > 0 
        ? filteredResult.map((key:IBtnOption) => <CustomCheckbox initialValue={modalData.selectedColumns.indexOf(key.id) !== -1}  simple={true} key={key.name} handleOnSelectRole={handleOnSelectColumn} id={key.id} text={key.name}/>)
        : <div>No data available</div>;
    }
    if (source === "source_data") {
      let filteredResult:IBtnOption[] = [];
      if (mode === "onlySelected") {
        result.sourceDataKeysExtr.forEach((col:IBtnOption) => {
          if (col.id !== "threshold" && modalData.selectedColumns.indexOf(col.id) !== -1) {
            filteredResult.push(col);          
          } 
        });
        filteredResult = filterByVal? filterArrOfObjByString(filteredResult, "name", filterByVal) : filteredResult;
      }else {
        result.sourceDataKeysExtr.forEach((col:IBtnOption) => {
          if (col.id !== "threshold" && !selectedModalFilteringOptions.some((selectedOption) => !col.id.includes(selectedOption))) {
            filteredResult.push(col);
          } 
        });
        filteredResult = filterByVal? filterArrOfObjByString(filteredResult, "name", filterByVal) : filteredResult;
      }
      return filteredResult.length > 0 
        ? filteredResult.map((key: IBtnOption) => (
        <CustomCheckbox
          initialValue={modalData.selectedColumns.indexOf(key.id) !== -1}
          simple={true}
          key={key.name}
          handleOnSelectRole={handleOnSelectColumn}
          id={key.id}
          text={key.name}
        />
          ))
        : <div>No data available</div>;
    }
    if (source === "history") {
      if (modalData.widget_type === "chart"){
        // chart
        // we display threshhold as x-axis
        let filteredResult:IBtnOption[] = [];

        if (mode === "onlySelected") {
          result.historyKeysExtr.forEach((col:IBtnOption) => {
            if (col.id !== "threshold") {
              if (modalData.selectedColumns.indexOf(col.id) !== -1) {
                filteredResult.push(col);
              }
            } 
          });
        } else {
          result.historyKeysExtr.forEach((col:IBtnOption) => {
            if (col.id !== "threshold" && !selectedModalFilteringOptions.some((selectedOption) => !col.id.includes(selectedOption))) {
              filteredResult.push(col);
            } 
          });
          filteredResult = filterByVal? filterArrOfObjByString(filteredResult, "name", filterByVal) : filteredResult;
        }
        return filteredResult.map((key:IBtnOption) => {
          return (
            <CustomCheckbox
              withInnerHeight={"#modalInputsWrapper"}
              lastSelectedPicker={lastSelectedPicker}
              setLastSelectedPicker={(keyId:string) => setLastSelectedPicker(keyId)}
              handleOnSelectColumnColor={handleOnSelectColumnColor}
              selectedColor={modalData.customColumnsColors?.[key.id] ?? ""}
              defaultColor={resultsColsGeneratedColors?.[key.id] ?? ""}
              initialValue={modalData.selectedColumns.indexOf(key.id) !== -1}
              simple={true}
              key={key.name}
              handleOnSelectRole={handleOnSelectColumn}
              id={key.id}
              text={key.name}/>
          );
        });
      } else {
        // table
        let filteredResult:IBtnOption[] = [];

        if (mode === "onlySelected") {
          result.historyKeysExtr.forEach((col:IBtnOption) => {
            if (modalData.selectedColumns.indexOf(col.id) !== -1) {
              filteredResult.push(col);
            }
          });
        } else {
          result.historyKeysExtr.forEach((col:IBtnOption) => {
            let matched = false;
            selectedModalFilteringOptions.forEach((selectedOption) => {
              if (!col.id.includes(selectedOption)) {
                matched = true;
              } 
            });
            if (!matched) {
              filteredResult.push(col);
            }
          });
  
          filteredResult = filterByVal? filterArrOfObjByString(filteredResult, "name", filterByVal) : filteredResult;
        }

        return filteredResult.map((key:IBtnOption) => <CustomCheckbox initialValue={modalData.selectedColumns.indexOf(key.id) !== -1}  simple={true} key={key.name} handleOnSelectRole={handleOnSelectColumn} id={key.id} text={key.name}/>);
      }
    }
  };

  const memorizedPrint = useMemo(() => {
    let modalFields:IColumn[] = [];
    
    if (modalData.addAfter) {
      modalFields = [
        {
          key: "name",
          displayName: "Widget type",
          dataType: {
            type: "enumeration",
            values: [{ name: "Text", id: "Text" }, { name: "Custom widget", id: "Custom widget" }]
          }
        }
      ];
    }

    if (Object.keys(modalData).length !== 0 && modalData.constructor === Object ) {
      if (modalData.name === "Text") {
        modalFields = [
          {
            key: "textType",
            displayName: "Text type",
            dataType: {
              type: "enumeration",
              values: textWidgetOptionsReportPDF
            }
          },
        ];
        if (modalData.textType === "text") {
          modalFields = [
            ...modalFields,
            {
              key: "text",
              displayName: "Text",
              dataType: {
                type: "textarea",
                size: "large"
              }
            },
          ];
        } else if (modalData.textType === "header" || modalData.textType === "subheader") {
          modalFields = [
            ...modalFields,
            {
              key: "text",
              displayName: "Text",
              dataType: {
                type: "text",
              }
            },
          ];
        }
      } else if (modalData.name === "Custom widget") {
        modalFields = [
          {
            key: "source",
            displayName: "Source",
            dataType: {
              type: "enumeration",
              values: reportWidgetSources
            },
            extraClass: "half"
          },
          {
            key: "widget_type",
            displayName: "Widget type",
            dataType: {
              type: "enumeration",
              values: modalData.source === "report_data" || modalData.source=== "history" ? widgetOptions : [{ name: "Table", id: "table" }]
            },
            extraClass: "half"
          }
        ];
      }

      let allMetrics = [
        [
          {
            metrics: [...coreMetrics],
            excludeOnSelect: ["alert", "transaction", "consolidated" , "entity"]
          },
          {
            metrics: [...subCoreMetrics],
            excludeOnSelect: ["amount", "count"]
          },
          {
            metrics: [...subCoreDetailsMetrics],
            excludeOnSelect: ["tp", "tn", "_p", "fpr", "fp", "fn"]
          },
        ]
      ];

      let allMetricsRight = [
        {
          metrics: [...metrics6],
          excludeOnSelect: ["precision", "recall", "specificity"]
        },
      ];
      if (modalData.source !== "history") {
        allMetricsRight = [{
          metrics: [...metrics4],
          excludeOnSelect: ["test", "train", "tvtr"]
        },
        {
          metrics: [...metrics5],
          excludeOnSelect: ["median", "min", "max", "mean", "std"]
        }, ...allMetricsRight,];
      }

      allMetrics = [...allMetrics, allMetricsRight];

      return (
        <Fragment>
          <div onScroll={() => {setLastSelectedPicker("scrollAction");}} style={{paddingRight: `${modalData?.steps?.length >= 6? "15px" : ""}`}} id="modalInputsWrapper" className="reportDetails-modalInputsWrapper">
            <RenderFields 
              fields={[...modalFields]} data={modalData} 
              handleOnChange={handleOnChange}
            />
            {
              modalData.source === "report_data" && modalData.widget_type === "table" &&
                <div className="reportDetails-thresholdSelection">
                  <EnumerationField
                    key={"show_only_for_threshold"}
                    data={activeOptions}
                    inputKey={"show_only_for_threshold"}
                    label={"Show only for model threshold"}
                    handleOnChange={handleOnChange}
                    required={true}
                    autoFocus={false}
                    defaultValue={modalData.show_only_for_threshold}
                  />
                </div>
            }
            {modalData.source && modalData.widget_type? <Fragment>
              {(modalData.source === "report_data" || modalData.source === "history") && <Fragment>
                <div className="reportDetails-metricsLabel">Filter metrics</div>

                {allMetrics.map((metricCol, i) => {
                  return (
                    <div key={i} className="reportDetails-aggregateBtns1">                    
                      {metricCol.map((metricData, i) => (
                        <div key={i}>
                          {metricData.metrics.map((optionData, i) => {
                            return (
                              <CustomCheckbox
                                simple={true}
                                key={i}
                                handleOnSelectRole={() => {
                                  setSelectedModalFilteringOptions((prev) => {
                                    if (prev.indexOf(optionData.key) === -1) {
                                      const result = prev.filter((opt) =>  {
                                        return metricData.excludeOnSelect.indexOf(opt) === -1;
                                      });
                                      return [...result, optionData.key];
                                    } else {
                                      return prev.filter((opt) => opt !== optionData.key);
                                    }
                                  });
                                }}
                                id={optionData.key} 
                                text={optionData.label}
                                initialValue={selectedModalFilteringOptions.indexOf(optionData.key) !== -1}
                              />
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  );
                })}

              </Fragment>}

              <TextInput
                type={"text"}
                extraClass={modalData.source === "history" ? "half" : ""}
                id={"reportDetails-searchUserInput"}
                required={false}
                inputKey={"filterColumnsWith"}
                autofocus={false}
                placeholder={"Type to filter"}
                handleOnChange={(targetVal) => setFilterByVal(targetVal)}
              />

              {modalData.source === "history" ?
                <TextInput
                  label="Threshold"
                  type={"number"}
                  subType= "number"
                  allowedByParent={true}
                  controlled={true}
                  value={modalData?.selectedThreshold ? Number(modalData?.selectedThreshold) : ""}
                  inputTypeLimit={1}
                  extraClass={modalData.source === "history" ? !unblockInput? "" : "half" : ""}
                  id={"reportDetails-searchUserInput"}
                  required={true}
                  inputKey={"selectedThreshold"}
                  autofocus={false}
                  defineStep={() => defineStep(getHistoryColsData?.rows.thresholds)}
                  decimalsFormatting={getHistoryColsData?.rows.thresholds? getHistoryColsData?.rows.thresholds : 11}
                  placeholder={`e.g. ${defineStep()}`}
                  handleOnChange={handleOnChange}
                /> : ""}

              <div>
                <div className="reportDetails-popup-buttonsWrapper">
                  {widgetColumnsBtns(modalData.source)}
                </div>

                <span className={"reportDetails-selectedColumsPoolWrapper"}>
                  <div className={"metricsPoolTitle"}>Selected metrics: </div>
                  {modalData.selectedColumns.length !== 0 ? widgetColumnsBtns(modalData.source, "onlySelected") : <span className="reportDetails-noMetricsMsgs">No metrics have been selected</span>}
                </span>    
              </div>
            </Fragment> : ""}

          </div>

        </Fragment>
      );
    }
  }, [modalData, extKeys, filterByVal, lastSelectedPicker, resultsColsGeneratedColors, selectedModalFilteringOptions, resultDataColsWithLabelsTransformed, setLastSelectedPicker]);

  const generatePdfHandler = (mode:string) => {
    const prepareOrdering = layoutItems.map((item: IBlock) => {
      const findLayout = layout.find(layoutElement => layoutElement.i === item.id);
      if (item.optionName === "Text") {
        return {
          type: item.textType?.toLowerCase(),
          y: findLayout?.y || 0,
          content: item.text,
        };
      } else if (item.settings?.source === "history") {
        return {
          y: findLayout?.y || 0,
          source: item.settings?.source,
          type: item.settings?.widget_type.toLowerCase(),
          columns: item.settings?.selectedColumns.map((col:string) => {
            return {
              header: historyDataColsWithLabels?.[col] ? historyDataColsWithLabels?.[col] : convertKeyIntoName(col), 
              key: col,
              custom_color: item.settings?.customColumnsColors[col]
            };}),
          threshold: item.settings?.selectedThreshold,
        };
      }
      else {
        return {
          y: findLayout?.y || 0,
          source: item.settings?.source,
          type: item.settings?.widget_type.toLowerCase(),
          show_only_for_threshold: item.settings?.show_only_for_threshold,
          columns: item.settings?.selectedColumns.map((col:string) => {
            return {
              header: resultDataColsWithLabels?.[col] ? resultDataColsWithLabels?.[col] : convertKeyIntoName(col),
              key: col,
              custom_color: item.settings?.customColumnsColors[col]
            };})
        };
      }
    });
    const sortedResult = prepareOrdering.sort((a, b) => a.y - b.y);
    const cleanedUp  = sortedResult.map((item: IPreparedItem) => {
      const result = {...item};
      delete result["y"];
      return result;
    });
    if (mode === "download") {
      postGeneratePdf(`report/${params.id}/pdf`, {report_id: params.id, content: cleanedUp}, undefined, "none");
    } else if (mode === "preview") {
      postGeneratePdfPreview(`report/${params.id}/pdf`, {report_id: params.id, content: cleanedUp}, undefined, "none");
    }
  };

  const memorizedPrintPreview = () => {
    return (
      <Fragment>
        {preview && <Preview data={preview.blob} />}
      </Fragment>
    );
  };

  const handlePreviewModalClosed = () => {
    setPreviewModal(false);
  };

  const handlePreviewSubmit = () => {
    if (preview) {
      const url = window.URL.createObjectURL(preview.blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", preview.filename);
      document.body.appendChild(link);
      link.click();
      setPreviewModal(false);
    }
  };

  const saveLayoutHandler = (row?: { id: string; name: string; is_installation: boolean; is_global: boolean; tenant_default: boolean }) => {
    if (!reportDetailsData.selected_pdf_template_layout_id && !row) {
      setSaveTemplateModal({...saveTemplateModal, show: true});
    } else {
      const defineId = reportDetailsData.selected_pdf_template_layout_id ? reportDetailsData.selected_pdf_template_layout_id : "";
      if (row?.id) {
        postPdfTemplate(`template_layout/${row.id}`, 
          {
            id: row.id, 
            name: row.name, 
            layout: JSON.stringify({layout: layout, layoutItems: layoutItems, lock: true }),
            archived: false,
            is_installation: row.is_installation,
            is_global: row.is_global,
            tenant_default: row.tenant_default,
            process: "report",
            template_type: "analytics_report_pdf_template"
          }, undefined, "none");
        postSelectTemplateLayout(`select_template_layout/${reportDetailsData.dataset_id}/analytics_report_pdf_template`,{
          selected_template_layout_id: row.id
        }, undefined, "none");
        setModalState({
          isOpen: false,
          rowId: undefined,
        });
      } else if (reportDetailsData.selected_pdf_template_layout_id) {
        postPdfTemplate(`template_layout/${reportDetailsData.selected_pdf_template_layout_id}`, 
          {
            id: reportDetailsData.selected_pdf_template_layout_id, 
            name: reportDetailsData.selected_pdf_template_layout_name, 
            layout: JSON.stringify({layout: layout, layoutItems: layoutItems, lock: true }),
            archived: false,
            is_installation: pdfTemplateData?.is_installation,
            is_global: pdfTemplateData?.is_global,
            tenant_default: pdfTemplateData?.tenant_default,
            process: "report",
            template_type: "analytics_report_pdf_template"
          }, undefined, "none");
        postSelectTemplateLayout(`select_template_layout/${reportDetailsData.dataset_id}/analytics_report_pdf_template`,{
          selected_template_layout_id: defineId
        }, undefined, "none");
      }
    }
  };
  const handleSubmitTemplateAsNew = function () {
    if (!saveAsNewTemplateModal.template) { 
      return;
    }

    const newId = createUUID();
    setLatestTemplateId(newId);
  
    const updatedLayout = JSON.stringify({ layout, layoutItems, lock: true });
  
    postPdfTemplate(
      `template_layout/${newId}`,
      {
        id: newId,
        name: saveAsNewTemplateModal.template,
        layout: updatedLayout,
        tenant_default: false,
        archived: false,
        is_installation: false,
        is_global: false,
        process: "report",
        template_type: "analytics_report_pdf_template",
      },
      "Template created successfully"
    );
  
    setSaveAsNewTemplateModal({
      show: true,
      template: saveAsNewTemplateModal.template,
      template_id: newId,
    });
  };

  const handleDiscard = () => {
    getTemplateLayout(
      `template_layout/${reportDetailsData.selected_pdf_template_layout_id}`,
      undefined,
      undefined,
      "Template reverted successfully"
    );
    setLock(true);
    setIsDirty(false);
    setFlag(false);
    setModalState({ isOpen: false, rowId: undefined });
    next();
  };

  const onBackClick = () => {
    if (isDirty) {
      setModalState({ isOpen: true, rowId: undefined });
    } else {
      handleBack();
    }
  };

  const CustomModal: React.FC<ICustomModalProps> = ({
    title,
    children,
  }) => {
    return (
      <div className="modal show d-block save-modal">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Save changes on template: {getTemplateLayoutData?.name ? `${getTemplateLayoutData.name}` : title}
              </h5>
              <button type="button" className="btn-close" onClick={() => {
                setModalState({
                  isOpen: false,
                  rowId: undefined,
                });
                setFlag(false);
              }}>
              </button>
            </div>
            <div className="modal-body">{children}</div>
            <div className="modal-footer">
              {getTemplateLayoutData?.is_global || getTemplateLayoutData?.sygno_default ? 
                <button type="button" className="btn btn-danger" onClick={handleDiscard}>Discard</button> :
                <Fragment>
                  {!getTemplateLayoutData?.sygno_default && 
                    <button type="button" className="btn btn-primary" 
                      onClick={() => saveLayoutHandler(getTemplateLayoutData)}>
                      Save 
                      </button>
                  }
                  <button type="button" className="btn btn-danger" onClick={handleDiscard}>
                    Discard
                  </button>
                </Fragment>
              }
              <button
                type="button"
                className="btn btn-save-new"
                onClick={() => {
                  setSaveAsNewTemplateModal({ ...saveAsNewTemplateModal, show: true });
                }}
              >
                Save as new template
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  function getModalContent(): React.ReactNode {
    return (
      <div>
        <p>You have unsaved changes. Do you want to save them before leaving?</p>
      </div>
    );
  }
  return (
    <div className="createPdf">
    <SimpleModal 
        modalBody={saveAsNewTemplateTemplateModal}
        buttonText={"Save"}
        mode={"Save as New Template"}
        handleModalClosed={() => setSaveAsNewTemplateModal({...saveAsNewTemplateModal, show: false})} 
        handleSubmit={handleSubmitTemplateAsNew} 
        show={saveAsNewTemplateModal.show}
          />
      {(flag || modalState.isOpen) && <CustomModal 
        show={flag || modalState.isOpen}
        onHide={() => {
          setModalState({
            isOpen: false,
            rowId: undefined,
          });
        }}
        title={
          "Confirm Action"
        }
        confirmButtonText={""}
        onConfirm={undefined}>{getModalContent()}</CustomModal>}
        
      <SimpleModal wrapperClass={"reportDetails-featureSelectionModal"} onModalClickHandler={onModalClickHandler} modalBody={memorizedPrint} buttonText={"Save"} mode={"Widget settings"} handleModalClosed={handleModalClosed} handleSubmit={handleSubmit} show={modal}/>
      {generatePdfPreviewStatusPost === "completed" && <SimpleModal type="large" wrapperClass={"pdfModal"} modalBody={memorizedPrintPreview()} buttonText={"Download"} mode={"PDF Preview"} handleModalClosed={handlePreviewModalClosed} handleSubmit={handlePreviewSubmit} show={previewModal}/>}

      {validateLoadingStatusNew([modelDataStatus, getHistoryColsStatus, getResultDataByColsStatus, generatePdfPreviewStatusPost, getResultsColsStatus, getResultDataByColsStatus, sourceDataStatus, pdfTemplateStatusGet, pdfTemplateStatusPost, reportDetailsStatus, generatePdfStatusPost]) && <Spinner />}
      
      {unblockInput && <div className="createPdf-btnWrapper">
        <button 
          onClick={() => onBackClick()}
          className="backButton">
          <BsArrowLeft  />
        </button>

        {!lock && renderOptionsBtns(optionsBlocks)}
        <ToolTipGeneralInfo
          
          content={"Preview as PDF"}
          direction="top"
          delay={350}
        >
          <Button 
            onClick={() => {
              generatePdfHandler("preview");
              setPreviewModal(true);
            }} 
            className="blank">
            <BsFillEyeFill />
          </Button>
        </ToolTipGeneralInfo>

        <ToolTipGeneralInfo
          content={"Download as PDF"}
          direction="top"
          delay={350}
        >
          <Button onClick={() => generatePdfHandler("download")} className="blank">
            <BsDownload />
          </Button>
        </ToolTipGeneralInfo>

        {!lock && <ToolTipGeneralInfo
          content={"Lock the editor"}
          direction="top"
          delay={350}
        >
          <Button
            disabled={!unblockInput}
            onClick={() => {
                setModalState({ isOpen: true, rowId: reportDetailsData.selected_pdf_template_layout_id });
                setLock(true);
            }}
            className="blank"
          >
            <BsFillUnlockFill />
          </Button>
        </ToolTipGeneralInfo>}

        {lock && <ToolTipGeneralInfo
          content={"Unlock the editor"}
          direction="top"
          delay={350}
        >
          <Button
            disabled={!unblockInput}
            onClick={() => {
              setLock(!lock);
            }}
            className="blank"
          >
            <BsLockFill />
          </Button>
        </ToolTipGeneralInfo>}
      </div>}
      <div  className="createPdf-gridWrapper">
        <div className="createPdf-page">
          <div className="createPdf-subpage">
            <WidgetsLayout
              addAfter={addAfter}
              mode={"pdf"}
              layout={layout}
              layoutItems={layoutItems}
              getLayoutChanges={getLayoutChanges}
              getLayoutItems={(data) => setlayoutItems(data)}
              optionsBlocks={optionsBlocks}
              lock={lock}
              setModal={setModal}
              setModalDataFromBlock={setModalDataFromBlock}
              unblockInput={unblockInput}
              mainLayoutParent={[]}
              minWidth={36}
              blockMargin={[30,30]}
            />
          </div>
        </div>
      </div>

    </div>
  );
};

export default CreatePdf;
